import {
  ArrowRight,
  CheckCircle2,
  Zap,
  GraduationCap,
  FlaskConical,
  MessageSquareText,
  Layers,
  Cloud,
  Boxes,
  Code2,
  FileCode2,
  Bot,
  Github,
  Hammer,
  Swords,
  Siren,
  Workflow,
  KeyRound,
  Lock,
  Cpu,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { TrinetraLoop3 } from '@/components/icons/trinetra-icons';
import { SIGNUPS_ENABLED, REQUEST_ACCESS_MAILTO } from '@/config/features';

/**
 * Trinetra app landing (served at trinetra.nirvik.ai).
 *
 * Hybrid page: product pitch on top, founder motivation in the middle,
 * capability story below. Draws copy and structure from:
 *   - site-nirvik (April 7 inflection point, builder + hacker thesis)
 *   - site-trinetra (agent swarm, feature trio, how-it-works)
 *
 * Palette:
 *  - Background: theme `--background` (warm ivory / deep indigo).
 *  - Brand + CTAs: turmeric (`--primary`).
 *  - Labs announcement banner: Indian-flag saffron → chakra-navy gradient.
 *  - A 3-band tricolor strip sits just above the footer as a quiet identity
 *    cue without being kitsch.
 */
export function LandingPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* ─── Announcement Banner ─── flag-inspired gradient. Hardcoded
       * Tailwind palette (not theme tokens) because brand tokens lift in
       * dark mode and would break white-text contrast. All three stops
       * hit AA+ on white in both modes. */}
      <Link
        to="https://learning.nirvik.ai"
        className="block bg-gradient-to-r from-red-900 via-amber-700 to-emerald-800 text-white text-center py-2.5 px-4 text-xs sm:text-sm font-medium hover:opacity-95 transition-opacity"
      >
        <span className="inline-flex items-center justify-center gap-2 flex-wrap">
          <FlaskConical className="h-4 w-4 shrink-0" />
          <span>
            Announcing <strong>Nirvik AI Learning</strong>
            <span className="hidden sm:inline"> — Building the next generation of cyber defenders</span>
          </span>
          <ArrowRight className="h-3.5 w-3.5 shrink-0" />
        </span>
      </Link>

      {/* ─── Nav ─── */}
      <nav className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 max-w-6xl mx-auto gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <TrinetraLoop3 className="h-7 w-7 sm:h-8 sm:w-8 text-primary-strong shrink-0" />
          <span className="font-display text-xl sm:text-2xl font-semibold tracking-wide text-foreground truncate">
            Trinetra
          </span>
        </div>
        <div className="hidden md:flex items-center gap-7 text-sm text-muted-foreground">
          <a href="#capabilities" className="hover:text-foreground transition-colors">Capabilities</a>
          <a href="#agents" className="hover:text-foreground transition-colors">Agents</a>
          <a href="#how" className="hover:text-foreground transition-colors">How it works</a>
          <a href="https://nirvik.ai" className="hover:text-foreground transition-colors">Nirvik AI Labs</a>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <Link
            to="/login"
            className="hidden sm:inline-block text-sm text-muted-foreground hover:text-foreground px-3 py-2 transition-colors"
          >
            Sign In
          </Link>
          {SIGNUPS_ENABLED ? (
            <Link
              to="/signup"
              className="text-sm font-medium bg-primary text-primary-foreground px-3 sm:px-4 py-2 rounded-lg hover:opacity-90 transition-opacity whitespace-nowrap"
            >
              Get Started
              <span className="hidden sm:inline"> Free</span>
            </Link>
          ) : (
            <a
              href={REQUEST_ACCESS_MAILTO}
              className="text-sm font-medium bg-primary text-primary-foreground px-3 sm:px-4 py-2 rounded-lg hover:opacity-90 transition-opacity whitespace-nowrap"
            >
              Request Access
            </a>
          )}
        </div>
      </nav>

      {/* ─── Hero ─── */}
      <section className="relative px-4 sm:px-6 py-16 md:py-24 max-w-4xl mx-auto text-center">
        <div className="absolute inset-0 -z-10 flex items-start justify-center pointer-events-none">
          <div className="w-[600px] h-[400px] bg-halo-turmeric opacity-60" />
        </div>

        <div className="inline-flex items-center gap-2 bg-accent text-accent-foreground px-3 py-1 rounded-full text-xs sm:text-sm font-medium mb-6 border border-border">
          <Zap className="h-4 w-4 text-primary-strong shrink-0" />
          The agentic AI platform for the new age of security
        </div>
        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-semibold text-foreground leading-[1.05] mb-6">
          Building cyber defenses
          <br />
          <span className="text-primary-strong">for the agentic era</span>
        </h1>
        <p className="text-base sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">
          Accessible to every builder at any stage. Trinetra deploys a swarm of specialized AI agents
          that scan your code, infrastructure, and cloud to identify vulnerabilities and opportunities.
          Optionally, agents can automatically fix issues and harden your defenses.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
          {SIGNUPS_ENABLED ? (
            <Link
              to="/signup"
              className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-5 sm:px-6 py-3 rounded-lg text-base sm:text-lg font-medium hover:opacity-90 transition-opacity shadow-sm"
            >
              Start Scanning Free
              <ArrowRight className="h-5 w-5 shrink-0" />
            </Link>
          ) : (
            <a
              href={REQUEST_ACCESS_MAILTO}
              className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-5 sm:px-6 py-3 rounded-lg text-base sm:text-lg font-medium hover:opacity-90 transition-opacity shadow-sm"
            >
              Request Access
              <ArrowRight className="h-5 w-5 shrink-0" />
            </a>
          )}
          <a
            href="#how"
            className="inline-flex items-center justify-center gap-2 bg-secondary text-foreground px-5 sm:px-6 py-3 rounded-lg text-base sm:text-lg font-medium border border-border hover:bg-accent transition-colors"
          >
            See how it works
          </a>
        </div>

        <p className="mt-8 text-xs text-muted-foreground inline-flex items-center gap-2">
          <CheckCircle2 className="h-3.5 w-3.5 text-india-green" />
          {SIGNUPS_ENABLED
            ? 'Free to start · No credit card · Built for builders'
            : 'Currently onboarding in closed beta · Request access to try it'}
        </p>
      </section>

      {/* ─── Why we built this ───
       * Founder motivation, mirrored from nirvik.ai. The April 7 inflection
       * point and the builder + hacker thesis — kept tight since this is
       * the app, not the parent marketing site. */}
      <section className="px-4 sm:px-6 py-16 md:py-20 max-w-3xl mx-auto">
        <h2 className="text-xs font-bold uppercase tracking-[0.22em] text-primary-strong mb-6 text-center">
          Why we built this
        </h2>
        <div className="space-y-6 text-base sm:text-lg leading-relaxed text-foreground/90">
          <p>
            In April 2026, a frontier model autonomously discovered zero-day vulnerabilities hidden for decades —
            a 27-year-old bug in OpenBSD, a 17-year-old remote root in FreeBSD, over 1,000 critical
            vulnerabilities surfaced in a single research cycle. The era of AI-native cybersecurity arrived overnight.
          </p>
          <p>
            Offense moved. Defense has to move faster — and smarter.
            That means building systems with defense in depth and zero trust from the inside out,
            shifting security left into every build pipeline and every development workflow.
          </p>
        </div>

        <div className="mt-10 pl-5 sm:pl-7 border-l-[3px] border-primary/70 relative">
          <div className="absolute -left-[14px] top-0 h-7 w-7 rounded-full bg-background border-[3px] border-primary/70 flex items-center justify-center">
            <Hammer className="h-3 w-3 text-primary-strong" />
          </div>
          <p className="text-lg sm:text-xl font-semibold text-foreground leading-snug">
            Trinetra is our answer — a <span className="text-primary-strong">builder + hacker</span> approach
            to autonomous cyber defense, built for the age of agentic AI.
          </p>
        </div>
      </section>

      {/* ─── Capabilities / feature trio ─── */}
      <section id="capabilities" className="px-4 sm:px-6 py-14 md:py-20 bg-muted/50">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-center text-foreground mb-3">
            Security made simple
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground text-center max-w-2xl mx-auto mb-10 md:mb-14">
            Built for vibe coders, indie hackers, and every builder shipping in the AI-assisted era. If you can ship, you can secure.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                icon: MessageSquareText,
                title: 'Plain-Language Findings',
                description: 'No jargon, no cryptic codes. Every finding is explained like a mentor would — what it is, why it matters, exactly how to fix it.',
                accent: 'india-green' as const,
              },
              {
                icon: Zap,
                title: 'Point it at anything',
                description: 'URLs, API endpoints, repos, cloud accounts — or any combination. Trinetra detects what you have and picks the right skills to scan it.',
                accent: 'turmeric' as const,
              },
              {
                icon: GraduationCap,
                title: 'Learn As You Go',
                description: 'Every finding links to a short lesson so you understand the concept — not just the fix. Security literacy that compounds across every scan.',
                accent: 'chakra-navy' as const,
              },
            ].map((feature) => {
              const iconBg =
                feature.accent === 'india-green'
                  ? 'bg-india-green/15 text-india-green'
                  : feature.accent === 'chakra-navy'
                    ? 'bg-chakra-navy/15 text-chakra-navy dark:text-chakra-navy'
                    : 'bg-primary/15 text-primary-strong';
              return (
                <div
                  key={feature.title}
                  className="bg-card text-card-foreground rounded-xl p-6 border border-border shadow-sm"
                >
                  <div className={`h-12 w-12 rounded-lg flex items-center justify-center mb-4 ${iconBg}`}>
                    <feature.icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-foreground mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── Agent swarm ───
       * The distinctive technical story — specialized agents, not a
       * monolith. Counts are credible, not inflated. */}
      <section id="agents" className="px-4 sm:px-6 py-16 md:py-24 max-w-6xl mx-auto">
        <div className="text-center mb-12 md:mb-14">
          <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/25 text-primary-strong px-3 py-1 rounded-full text-xs sm:text-sm font-medium mb-5">
            <Bot className="h-4 w-4" />
            Agentic architecture
          </div>
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-foreground mb-4">
            A swarm of specialists, not a monolith
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
            Trinetra runs <span className="font-semibold text-foreground">32 specialized skills</span> in concert —
            each narrow, focused, and good at one job. Together they cover the whole security surface.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {[
            { icon: FileCode2, name: 'Code & Supply Chain', count: 8, color: 'text-india-green' },
            { icon: Swords, name: 'Pentest & Recon', count: 8, color: 'text-primary-strong' },
            { icon: Cloud, name: 'Cloud & Infrastructure', count: 5, color: 'text-chakra-navy' },
            { icon: Layers, name: 'Posture & Governance', count: 5, color: 'text-saffron' },
            { icon: Siren, name: 'Threat & Response', count: 3, color: 'text-primary-strong' },
            { icon: Workflow, name: 'CI/CD & Orchestration', count: 3, color: 'text-chakra-navy' },
          ].map((agent) => (
            <div
              key={agent.name}
              className="flex items-center gap-4 p-5 rounded-xl bg-card border border-border hover:border-primary/30 transition-colors"
            >
              <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                <agent.icon className={`h-5 w-5 ${agent.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground">{agent.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{agent.count} specialists</p>
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-sm text-muted-foreground mt-10 max-w-2xl mx-auto">
          Findings are triaged, deduped, and translated — so you see what to fix, in your code, in your words.
        </p>
      </section>

      {/* ─── How it works ─── */}
      <section id="how" className="px-4 sm:px-6 py-16 md:py-24 bg-muted/50">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-center text-foreground mb-3">
            From connection to fix in under a minute
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground text-center mb-14 max-w-2xl mx-auto">
            Three steps. No setup. No SDK to install. No YAML to wrangle.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                step: '01',
                title: 'Connect your targets',
                description: 'Drop in a URL, an API endpoint, a repo, a cloud account — or any combination. Trinetra fingerprints what you have and picks the right skills.',
              },
              {
                step: '02',
                title: 'Agents scan in parallel',
                description: 'The swarm fans out across TLS, code, dependencies, cloud config, and runtime — all at once.',
              },
              {
                step: '03',
                title: 'Read a plain-English report',
                description: 'Each finding is ranked, explained, and paired with a concrete fix. Click through to the code or config that needs to change.',
              },
            ].map((s) => (
              <div key={s.step} className="relative p-6 rounded-xl bg-card border border-border">
                <div className="font-mono text-xs font-semibold text-primary-strong mb-4">{s.step}</div>
                <h3 className="font-display text-lg font-semibold text-foreground mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Integrations ─── */}
      <section className="px-4 sm:px-6 py-14 md:py-20 max-w-5xl mx-auto">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-muted-foreground text-center mb-8">
          Works where you work
        </p>
        <div className="flex flex-wrap items-center justify-center gap-x-10 sm:gap-x-14 gap-y-6 text-muted-foreground">
          {[
            { icon: Github, label: 'GitHub' },
            { icon: Cloud, label: 'AWS / GCP / Azure' },
            { icon: Code2, label: 'Any framework' },
            { icon: Boxes, label: 'Docker / K8s' },
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-2 text-sm font-medium">
              <item.icon className="h-5 w-5" />
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ─── Your keys, your environment ─── */}
      <section className="px-4 sm:px-6 py-16 md:py-24 bg-muted/50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12 md:mb-14">
            <div className="inline-flex items-center gap-2 bg-chakra-navy/10 border border-chakra-navy/25 text-chakra-navy px-3 py-1 rounded-full text-xs sm:text-sm font-medium mb-5">
              <KeyRound className="h-4 w-4" />
              Your keys. Your environment.
            </div>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold text-foreground mb-4">
              Deploy the way that fits your stack
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              Trinetra works with the LLM setup you already trust — bring your own key, route through private cloud inference, or run open-weights models locally.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {[
              {
                icon: KeyRound,
                title: 'Bring Your Own Key',
                description: 'Plug in your own Anthropic, OpenAI, or other provider key. Your keys, your billing, your rate limits — we never see them.',
                iconBg: 'bg-primary/15 text-primary-strong',
              },
              {
                icon: Lock,
                title: 'Private Cloud Inference',
                description: 'Route every agent call through AWS Bedrock or Google Vertex AI. Keep model traffic inside your VPC and compliance perimeter.',
                iconBg: 'bg-chakra-navy/15 text-chakra-navy',
              },
              {
                icon: Cpu,
                title: 'On-Prem & Local Models',
                description: 'Run open-weights models (Llama, Mistral, and more) inside your own environment. Data and inference stay behind your firewall.',
                iconBg: 'bg-india-green/15 text-india-green',
              },
            ].map((mode) => (
              <div
                key={mode.title}
                className="p-6 rounded-xl bg-card border border-border shadow-sm"
              >
                <div className={`h-12 w-12 rounded-lg flex items-center justify-center mb-4 ${mode.iconBg}`}>
                  <mode.icon className="h-6 w-6" />
                </div>
                <h3 className="font-display text-lg font-semibold text-foreground mb-2">{mode.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{mode.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="px-4 sm:px-6 py-16 md:py-20 bg-gradient-to-b from-background via-accent/30 to-background">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-foreground mb-4">
            {SIGNUPS_ENABLED ? 'Start scanning in 30 seconds' : 'Join the closed beta'}
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground mb-10">
            {SIGNUPS_ENABLED
              ? 'Free forever for your first project. No credit card. Upgrade when you need teams, private repos, or compliance reporting.'
              : 'We\'re onboarding early users and design partners. Drop us a note — we\'ll get you set up.'}
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            {SIGNUPS_ENABLED ? (
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-7 py-3.5 rounded-xl text-base font-semibold hover:opacity-90 transition-opacity shadow-sm"
              >
                Start Scanning Free
                <ArrowRight className="h-5 w-5" />
              </Link>
            ) : (
              <a
                href={REQUEST_ACCESS_MAILTO}
                className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-7 py-3.5 rounded-xl text-base font-semibold hover:opacity-90 transition-opacity shadow-sm"
              >
                Request Access
                <ArrowRight className="h-5 w-5" />
              </a>
            )}
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 bg-secondary text-foreground px-7 py-3.5 rounded-xl text-base font-semibold border border-border hover:bg-accent transition-colors"
            >
              Sign In
            </Link>
          </div>
        </div>
      </section>

      {/* ─── Tricolor strip ─── */}
      <div className="flex">
        <div className="h-px flex-1 bg-saffron" />
        <div className="h-px flex-1 bg-white" />
      </div>

      {/* ─── Footer ─── */}
      <footer className="px-4 sm:px-6 py-6 md:py-8 border-t border-border">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <TrinetraLoop3 className="h-5 w-5 text-primary-strong" />
            <span className="font-display text-sm font-semibold tracking-wide text-muted-foreground">Trinetra</span>
            <span className="text-xs text-muted-foreground/70 ml-2">
              A <a href="https://nirvik.ai" className="underline underline-offset-2 hover:text-foreground transition-colors">Nirvik AI Labs</a> product
            </span>
          </div>
          <p className="text-xs text-muted-foreground text-center sm:text-right">
            &copy; 2026 Nirvik AI Labs. Security for every builder.
          </p>
        </div>
      </footer>
    </div>
  );
}
